import {join} from '../../utils';
import {Component, createRef, CSSProperties, ReactNode, RefObject} from 'react';
import {fromEvent, interval, Subject, throttle, takeUntil} from 'rxjs';
import {isEqual, isFunction} from 'underscore';

import styles from './auto-size.module.less';

interface Bounds {
	width: number;
	height: number;
}

class State {
	availableWidth: number;
	availableHeight: number;
	initialized: boolean;
	maxWidth: number = 0;
	maxHeight: number = 0;
	contentWidth: number = 0;
	contentHeight: number = 0;
}

type RenderFunction = (bounds: Bounds) => ReactNode;

interface Props {
	width?: number;
	height?: number;
	maxWidth?: number | (() => number);
	maxHeight?: number | (() => number);
	contentWidth?: number | (() => number);
	contentHeight?: number | (() => number);
	children: ReactNode | RenderFunction;
}

export class AutoSize extends Component<Props, State> {
	public override state = new State();

	private mainElement: RefObject<HTMLDivElement> = createRef();
	private contentElement: RefObject<HTMLDivElement> = createRef();
	private readonly onDestroy$: Subject<void> = new Subject();

	public override componentDidMount() {
		fromEvent(window, 'resize')
			.pipe(
				takeUntil(this.onDestroy$),
				throttle(() => interval(30)),
			).subscribe(() => this.calcBounds());

		interval(500)
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(() => this.calcBounds());

		setTimeout(() => this.calcBounds());
	}

	public override componentDidUpdate(prevProps: Readonly<Props>) {
		if (prevProps.contentWidth !== this.props.contentWidth || prevProps.contentHeight !== this.props.contentHeight) {
			this.calcBounds();
		}
	}

	private calcBounds(width?: number, height?: number): void {
		const prevWidth = this.state.availableWidth;
		const prevHeight = this.state.availableHeight;
		if (!this.mainElement.current) {
			return;
		}

		const bounds = {
			width: this.mainElement.current.offsetWidth,
			height: this.mainElement.current.offsetHeight,
		};

		if (width && width <= bounds.width) {
			bounds.width = width;
		}

		if (height && height <= bounds.height) {
			bounds.height = height;
		}

		const maxBounds = this.calcMaxBounds();

		if (prevWidth !== bounds.width || prevHeight !== bounds.height || !isEqual(maxBounds, this.getMaxBounds())) {
			this.setState({
				initialized: true,
				availableWidth: bounds.width,
				availableHeight: bounds.height,
				...maxBounds,
			}, () => this.recheckBounds());
		}
	}

	private calcMaxBounds() {
		return {
			maxWidth: this.getValueOrCall(this.props.maxWidth),
			maxHeight: this.getValueOrCall(this.props.maxHeight),
			contentWidth: this.getValueOrCall(this.props.contentWidth),
			contentHeight: this.getValueOrCall(this.props.contentHeight),
		};
	}

	private getMaxBounds() {
		return {
			maxWidth: this.state.maxWidth,
			maxHeight: this.state.maxHeight,
			contentWidth: this.state.contentWidth,
			contentHeight: this.state.contentHeight,
		};
	}

	private recheckBounds() {
		if (!this.state.availableWidth && !this.state.availableHeight) {
			setTimeout(() => this.calcBounds());
		}
	}

	public get resizerStyles(): CSSProperties {
		const styles = {
			overflow: 'hidden',
			position: 'relative',
			width: '100%',
			height: '100%',
			display: 'flex',
		} as CSSProperties;

		if (this.state.initialized) {
			const {maxWidth, maxHeight, contentWidth, contentHeight} = this.getMaxBounds();

			if (maxWidth) {
				styles.maxWidth = maxWidth + 'px';
			}

			if (maxHeight) {
				styles.maxHeight = maxHeight + 'px';
			}

			if (contentWidth < maxWidth) {
				styles.maxWidth = contentWidth + 'px';
			}

			if (contentHeight < maxHeight) {
				styles.maxHeight = contentHeight + 'px';
			}
		}

		return styles;
	}

	public override render() {
		return <div className={join('resizer', this.state.initialized && styles.initialized)} style={this.resizerStyles}
		            ref={this.mainElement}>
			<div className={styles.overflowStyles}/>
			<div className={styles.contentStyles} ref={this.contentElement}>
				{this.state.initialized && this.renderChildren()}
			</div>
		</div>;
	}

	private renderChildren(): ReactNode {
		if (isFunction(this.props.children)) {
			const {maxWidth, availableWidth, contentWidth} = this.state;
			const {maxHeight, availableHeight, contentHeight} = this.state;
			let width = availableWidth;
			if(maxWidth > 0 && maxWidth < width) {
				width = maxWidth;
			}
			if(contentWidth > 0 && contentWidth < width) {
				width = contentWidth;
			}

			let height = availableHeight;
			if(maxHeight > 0 && maxHeight < height) {
				height = maxHeight;
			}
			if(contentHeight > 0 && contentHeight < height) {
				height = contentHeight;
			}

			return this.props.children({width, height});
		}
		return this.props.children;
	}

	private getValueOrCall(arg: number | (() => number)): number {
		if (typeof arg === 'function') {
			return arg();
		}

		return arg;
	}

	public override componentWillUnmount() {
		this.onDestroy$.next();
	}
}
